import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import {
  CaseSheetSection,
  BORDER,
  SECTION_BAR_BG,
} from "./CaseSheetModalComponents";

// ─── Toggle option sets ───────────────────────────────────────────────────────

const stretchabilityOptions = ["Good", "Poor"];
const textureOptions = ["Thickened", "Rough"];
const motivationOptions = ["High", "Moderate", "Poor"];

// ─── Tick lists (rendered as Yes/No rows) ─────────────────────────────────────

const PAST_MEDICAL_HISTORY_ITEMS = [
  ["Dyslipidaemia", "metabolic_pmh_dyslipidaemia"],
  ["Obesity", "metabolic_pmh_obesity"],
  ["Thyroid Disorder", "metabolic_pmh_thyroid_disorder"],
  ["Cardiovascular Disease", "metabolic_pmh_cardiovascular_disease"],
  ["Kidney Disease", "metabolic_pmh_kidney_disease"],
  ["Liver Disease", "metabolic_pmh_liver_disease"],
];

const FAMILY_HISTORY_ITEMS = [
  ["Diabetes", "metabolic_fh_diabetes"],
  ["Hypertension", "metabolic_fh_hypertension"],
  ["Heart Disease", "metabolic_fh_heart_disease"],
  ["Obesity", "metabolic_fh_obesity"],
];

const HABIT_ITEMS = [
  ["Smoking", "metabolic_habit_smoking"],
  ["Tobacco — Hookah", "metabolic_habit_tobacco_hookah"],
  ["Tobacco — Chewing", "metabolic_habit_tobacco_chewing"],
  ["Alcohol", "metabolic_habit_alcohol"],
  ["Menopause (if applicable)", "metabolic_habit_menopause"],
];

/** Foot findings are recorded per side, as marked on the proforma. */
const FOOT_EXAM_ITEMS = [
  ["Corn", "metabolic_foot_corn"],
  ["Callus", "metabolic_foot_callus"],
  ["Blister", "metabolic_foot_blister"],
  ["Ulcer", "metabolic_foot_ulcer"],
  ["Infection — Redness", "metabolic_foot_infection_redness"],
  ["Infection — Pus", "metabolic_foot_infection_pus"],
  ["Infection — Swelling", "metabolic_foot_infection_swelling"],
  ["Deformity — Claw Toes", "metabolic_foot_deformity_claw_toes"],
  ["Deformity — Hammer Toes", "metabolic_foot_deformity_hammer_toes"],
  ["Deformity — Hallux Valgus", "metabolic_foot_deformity_hallux_valgus"],
  ["Deformity — Flat Foot", "metabolic_foot_deformity_flat_foot"],
  ["Deformity — Charcot Foot", "metabolic_foot_deformity_charcot_foot"],
  ["Nails — Thickened", "metabolic_foot_nails_thickened"],
  ["Nails — Ingrown", "metabolic_foot_nails_ingrown"],
  ["Nails — Fungal", "metabolic_foot_nails_fungal"],
];

/** Column captions above the Left/Right checkbox pairs. */
const LeftRightHeader = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 185px) 1fr" },
      gap: { xs: 1, sm: 2 },
      mb: 1,
    }}
  >
    <Box />
    <Box sx={{ display: "flex", gap: 3, pl: { xs: 0, sm: 0.5 } }}>
      {["LEFT", "RIGHT"].map((side) => (
        <Typography
          key={side}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: SECTION_BAR_BG,
            letterSpacing: "0.6px",
            minWidth: 72,
          }}
        >
          {side}
        </Typography>
      ))}
    </Box>
  </Box>
);

const MetabolicClinicTab = ({
  renderTextField,
  renderYesNoField,
  renderSelectField,
  renderOptionToggleField,
  renderLeftRightCheckbox,
}) => {
  const renderYesNoList = (items) =>
    items.map(([label, key]) => renderYesNoField(label, key));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <CaseSheetSection title="CHIEF COMPLAINTS" defaultExpanded>
        {renderTextField(
          "Chief Complaints",
          "metabolic_chief_complaints",
          "Chief complaints"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="HISTORY OF PRESENT ILLNESS" defaultExpanded={false}>
        {renderTextField(
          "Duration",
          "metabolic_hpi_duration",
          "Duration of current illness",
          false
        )}
        {renderTextField("Symptoms", "metabolic_hpi_symptoms", "Symptoms")}
        {renderTextField(
          "Progression",
          "metabolic_hpi_progression",
          "Progression"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="PAST MEDICAL HISTORY" defaultExpanded={false}>
        {renderYesNoField("Diabetes Mellitus", "metabolic_pmh_diabetes")}
        {renderTextField(
          "Diabetes Duration",
          "metabolic_pmh_diabetes_duration",
          "e.g. 5 years",
          false
        )}
        {renderYesNoField("Hypertension", "metabolic_pmh_hypertension")}
        {renderTextField(
          "Hypertension Duration",
          "metabolic_pmh_hypertension_duration",
          "e.g. 3 years",
          false
        )}
        <Divider sx={{ borderColor: BORDER }} />
        {renderYesNoList(PAST_MEDICAL_HISTORY_ITEMS)}
        {renderTextField("Others", "metabolic_pmh_others", "Other conditions")}
      </CaseSheetSection>

      <CaseSheetSection title="FAMILY HISTORY" defaultExpanded={false}>
        {renderYesNoList(FAMILY_HISTORY_ITEMS)}
        {renderTextField("Others", "metabolic_fh_others", "Other conditions")}
      </CaseSheetSection>

      <CaseSheetSection title="SURGICAL HISTORY" defaultExpanded={false}>
        {renderTextField(
          "Surgical History",
          "metabolic_surgical_history",
          "Past surgeries with dates"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="LIFESTYLE ASSESSMENT" defaultExpanded={false}>
        {renderSelectField("Diet", "metabolic_diet")}
        {renderTextField(
          "Meal Timing",
          "metabolic_meal_timing",
          "Meal timing"
        )}
        {renderSelectField("Activity Level", "metabolic_activity_level")}
        {renderTextField("Sleep", "metabolic_sleep", "Sleep pattern & hours")}
        {renderSelectField("Stress", "metabolic_stress")}
      </CaseSheetSection>

      <CaseSheetSection title="HABITS" defaultExpanded={false}>
        {renderYesNoList(HABIT_ITEMS)}
      </CaseSheetSection>

      <CaseSheetSection title="VACCINATION STATUS" defaultExpanded={false}>
        {renderTextField(
          "Adult Vaccination",
          "metabolic_vaccination_status",
          "Vaccinations received"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="MEDICATION HISTORY" defaultExpanded={false}>
        {renderTextField(
          "Current Medications",
          "metabolic_current_medications",
          "Current medications"
        )}
        {renderSelectField("Compliance", "metabolic_medication_compliance")}
        {renderTextField(
          "Side Effects",
          "metabolic_medication_side_effects",
          "Medication side effects"
        )}
        <Divider sx={{ borderColor: BORDER }} />
        {renderTextField(
          "Drug Allergies",
          "metabolic_drug_allergies",
          "List drug allergies"
        )}
        {renderTextField(
          "Food Allergies",
          "metabolic_food_allergies",
          "List food allergies"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="CLINICAL EXAMINATION" defaultExpanded={false}>
        {renderTextField("Height (cm)", "metabolic_height", "cm", false)}
        {renderTextField("Weight (kg)", "metabolic_weight", "kg", false)}
        {renderTextField("BMI", "metabolic_bmi", "kg/m²", false)}
        {renderTextField(
          "Waist Circumference",
          "metabolic_waist_circumference",
          "cm",
          false
        )}
        {renderTextField(
          "Blood Pressure",
          "metabolic_blood_pressure",
          "e.g. 120/80 mmHg",
          false
        )}
        {renderTextField("Pulse", "metabolic_pulse", "bpm", false)}
        {renderTextField("SpO₂", "metabolic_spo2", "%", false)}
      </CaseSheetSection>

      <CaseSheetSection title="SKIN EXAMINATION" defaultExpanded={false}>
        {renderSelectField("Hydration", "metabolic_skin_hydration")}
        {renderOptionToggleField(
          "Stretchability",
          "metabolic_skin_stretchability",
          stretchabilityOptions
        )}
        {renderOptionToggleField(
          "Texture",
          "metabolic_skin_texture",
          textureOptions
        )}
      </CaseSheetSection>

      <CaseSheetSection title="FOOT EXAMINATION" defaultExpanded={false}>
        <LeftRightHeader />
        {FOOT_EXAM_ITEMS.map(([label, key]) =>
          renderLeftRightCheckbox(label, key)
        )}
      </CaseSheetSection>

      <CaseSheetSection title="DENTAL STATUS" defaultExpanded={false}>
        {renderSelectField("Teeth", "metabolic_teeth_status")}
        {renderSelectField("Gums", "metabolic_gums_status")}
      </CaseSheetSection>

      <CaseSheetSection title="LABORATORY INVESTIGATIONS" defaultExpanded={false}>
        {renderTextField(
          "Fasting Blood Sugar",
          "metabolic_lab_fbs",
          "mg/dL",
          false
        )}
        {renderTextField(
          "Post Prandial Blood Sugar",
          "metabolic_lab_ppbs",
          "mg/dL",
          false
        )}
        {renderTextField("HbA1c", "metabolic_lab_hba1c", "%", false)}
        {renderTextField(
          "Lipid Profile",
          "metabolic_lab_lipid_profile",
          "Lipid profile details"
        )}
        {renderTextField(
          "Liver Function Test",
          "metabolic_lab_lft",
          "LFT details"
        )}
        {renderTextField(
          "Kidney Function Test",
          "metabolic_lab_kft",
          "KFT details"
        )}
        {renderTextField(
          "Thyroid Profile",
          "metabolic_lab_thyroid_profile",
          "Thyroid profile details"
        )}
        {renderTextField("CBC", "metabolic_lab_cbc", "CBC details")}
        {renderTextField("FIB-4", "metabolic_lab_fib_4", "FIB-4 index", false)}
        {renderTextField("UACR", "metabolic_lab_uacr", "UACR", false)}
        {renderTextField(
          "Others",
          "metabolic_lab_others",
          "Other investigations"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="RISK STRATIFICATION" defaultExpanded={false}>
        {renderSelectField(
          "Cardiovascular Risk",
          "metabolic_risk_cardiovascular"
        )}
        {renderYesNoField(
          "Metabolic Syndrome",
          "metabolic_risk_metabolic_syndrome"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="DIAGNOSIS" defaultExpanded={false}>
        {renderTextField(
          "Primary (Provisional) Diagnosis",
          "metabolic_primary_diagnosis",
          "Primary diagnosis"
        )}
        {renderTextField(
          "Secondary (Definitive) Diagnosis",
          "metabolic_secondary_diagnosis",
          "Secondary diagnosis"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="TREATMENT PLAN" defaultExpanded={false}>
        {renderTextField(
          "Lifestyle Modification",
          "metabolic_lifestyle_modification",
          "Recommended lifestyle modifications"
        )}
        {renderTextField("Diet Plan", "metabolic_diet_plan", "Diet plan")}
        {renderTextField("Exercise", "metabolic_exercise", "Exercise plan")}
        {renderTextField(
          "Medications",
          "metabolic_medications",
          "Prescribed medications"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="FOLLOW-UP PLAN" defaultExpanded={false}>
        {renderTextField(
          "Next Visit",
          "metabolic_next_visit",
          "Next follow-up visit",
          false
        )}
        {renderTextField(
          "Monitoring",
          "metabolic_monitoring",
          "Monitoring plan"
        )}
      </CaseSheetSection>

      <CaseSheetSection title="PATIENT MOTIVATION" defaultExpanded={false}>
        {renderOptionToggleField(
          "Patient Motivation",
          "metabolic_patient_motivation",
          motivationOptions
        )}
      </CaseSheetSection>
    </Box>
  );
};

export default MetabolicClinicTab;
